import React, { useEffect, useRef } from 'react';
import styles from './TechnologiesSection.module.css';
import { MdKeyboardArrowRight } from 'react-icons/md';
import { FaTerminal } from 'react-icons/fa';
import Button from '../../Button/Button';
import { isSmallScreen } from '../../../utils/screen';
import { useTranslation } from '../../../hooks/useTranslation';
import { useNavigate } from 'react-router-dom';

interface TechnologiesSectionProps {
    title: React.ReactNode;
    technologies: Array<{
        name: string;
        logo: string;
    }>;
}

const TechnologiesSection: React.FC<TechnologiesSectionProps> = ({ title, technologies }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);
    
    // Use the first image from the technologies array
    const techImage = technologies[0]?.logo || '';
    
    useEffect(() => {
        const img = imgRef.current;
        const container = scrollContainerRef.current;
        
        if (!img || !container) return;
        
        const setAnimationProperties = () => {
            if (img.complete) {
                // Now we can access the natural width of the image
                const imageWidth = img.offsetWidth;
                
                // Set animation properties based on image width
                document.documentElement.style.setProperty('--image-width', `${imageWidth}px`);
            }
        };
        
        if (img.complete) {
            setAnimationProperties();
        } else {
            img.addEventListener('load', setAnimationProperties);
            return () => img.removeEventListener('load', setAnimationProperties);
        }
    }, [techImage]);
    
    return (
        <section className={styles.section}>
            <div className={styles.container}>
                <div className={styles.leftColumn}>
                    <div className={styles.titleWrapper}>
                        <FaTerminal size={isSmallScreen() ? 44 : 54} className={styles.terminalIcon} />
                        <div className={styles.titleContainer}>
                            <h2 className={styles.title}>{title}</h2>
                            <Button variant="outline" rightIcon={<MdKeyboardArrowRight size={24} />} className={styles.expertButton} onClick={() => navigate('/contact')}>
                                {t("common.contactUs")}
                            </Button>
                        </div>
                    </div>
                </div>
                <div className={styles.rightColumn}>
                    <div className={styles.scrollContainer} ref={scrollContainerRef}>
                        <div className={styles.scrollImages}>
                            <img ref={imgRef} src={techImage} alt="Technologies" className={styles.techImage} />
                            <img src={techImage} alt="Technologies" className={styles.techImage} />
                            <img src={techImage} alt="Technologies" className={styles.techImage} />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default TechnologiesSection; 